import { Controller } from "@hotwired/stimulus"

// Refreshes the org-chart turbo-frame while a build is running. Once the
// build finishes the server renders the ready/failed state (without this
// controller), so the final swap drops the poller and it stops on its own.
//
// While the build is STILL running, the canvas is not replaced. The old
// version swapped the whole frame's innerHTML every tick, which remounted the
// tree engine and re-fit the view — anyone exploring the growing chart was
// yanked back to the fit view, with their search and collapsed branches
// cleared, every four seconds for the length of the build. Instead the chrome
// around the canvas (progress strip, department chips, banners) is swapped
// and the live tree engine is asked to re-read its data in place, which keeps
// pan, zoom, search and collapse state (contact-tree#refresh loads with
// refit: false).
export default class extends Controller {
    static values = {
        url: String,
        interval: { type: Number, default: 4000 }
    }

    connect() {
        this.timer = setTimeout(() => this.poll(), this.intervalValue)
    }

    disconnect() {
        if (this.timer) clearTimeout(this.timer)
    }

    async poll() {
        const frame = this.element.closest("turbo-frame")
        if (!frame) return

        try {
            const response = await fetch(this.urlValue, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            const html = await response.text()
            const next = new DOMParser().parseFromString(html, "text/html").querySelector("turbo-frame#company_org_chart")
            // A response without the frame (an expired session's redirect, a
            // transient error page) used to end polling silently, leaving a
            // spinner that never resolved. Keep trying instead.
            if (!next) {
                this.timer = setTimeout(() => this.poll(), this.intervalValue)
                return
            }

            const oldCanvas = frame.querySelector(".contact-tree-page")
            const newCanvas = next.querySelector(".contact-tree-page")
            const stillBuilding = !!next.querySelector('[data-controller~="org-chart-poll"]')
            if (!stillBuilding || !oldCanvas || !newCanvas) {
                // Final state, or the first tick that has a canvas at all:
                // nothing worth preserving, one full swap. When the build is
                // still running this swapped-in HTML carries a fresh poller,
                // which schedules the next tick.
                frame.innerHTML = next.innerHTML
                return
            }

            // Swap everything around the canvas, in order, keeping the live
            // canvas element (and its Stimulus state) in place.
            const nextChildren = Array.from(next.children)
            const canvasIndex = nextChildren.indexOf(newCanvas)
            Array.from(frame.children).forEach((child) => { if (child !== oldCanvas) child.remove() })
            nextChildren.forEach((child, index) => {
                if (child === newCanvas) return
                if (index < canvasIndex) frame.insertBefore(child, oldCanvas)
                else frame.appendChild(child)
            })
            oldCanvas.dispatchEvent(new CustomEvent("org-chart:refresh"))
        } catch (_error) {
            this.timer = setTimeout(() => this.poll(), this.intervalValue)
        }
    }
}
